import React, { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import handleSetImage from '../../lib/handleSetImage'
import Button from '../common/Button'
import Icon from '../common/Icon'
import Loading from '../common/Loading'
import Sheet from '../common/Sheet'
import TextInput from '../common/TextInput'
import { useTranslation } from '../../lib/i18n'

const FeedbackImageUpload = ({ image, onRemove, onUpload }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)
  const [fileError, setFileError] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState('')

  const handleFileButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleImageUpload = (e) => {
    handleSetImage(
      e,
      (result) => {
        onUpload(result)
        setUploadStatus('Upload complete')
      },
      setFileError,
      (progress) => setUploadProgress(progress),
      (status) => setUploadStatus(status)
    )
  }

  return (
    <>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />
      {image ? (
        <div className="relative w-full min-h-48 h-48 max-h-48 mt-2 mb-6">
          <img
            src={image.preview}
            alt={t('common.uploadedPreview')}
            className="w-full h-full object-cover rounded-lg"
          />
          <button
            onClick={onRemove}
            className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-primary-20 text-white flex items-center justify-center"
            type="button"
            aria-label={t('common.removeImage')}
          >
            <Icon name="minus" className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <Button
            onClick={handleFileButtonClick}
            variant="outline"
            className="w-full"
          >
            {t('feedback.upload.button')}
          </Button>
          {fileError && <p className="text-sm text-red-500">{fileError}</p>}
          {(uploadProgress > 0 || uploadStatus) && (
            <div className="mt-2">
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="w-full h-2 bg-gray-200 rounded">
                  <div
                    className="h-full bg-primary rounded"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
              {uploadStatus && (
                <p className="text-sm text-gray-600 mt-1">{uploadStatus}</p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )
}

const FeedbackForm = ({
  control,
  errors,
  onSubmit,
  defaultReason,
  hideReasonSelector,
  onFileChange,
  image,
  onRemoveImage,
}) => {
  const { t } = useTranslation()
  const feedbackConfig = t('feedback')
  const FEEDBACK_TYPES = t('feedback.types')
  const DEFAULT_FEEDBACK_TYPE = t('feedback.default')

  return (
    <form
      onSubmit={onSubmit}
      className="w-full min-h-full h-full flex flex-grow flex-shrink-0 flex-col justify-start gap-2"
    >
      {!hideReasonSelector && (
        <Controller
          name="reason"
          control={control}
          rules={{ required: feedbackConfig.validation.reasonRequired }}
          render={({ field }) => (
            <select
              {...field}
              className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-20 focus:border-primary-20 appearance-none bg-white"
            >
              <option value="" disabled hidden>
                {t('feedback.selectAnOption')}
              </option>
              {Object.entries(FEEDBACK_TYPES).map(([value, { title }]) => (
                <option key={value} value={value}>
                  {title}
                </option>
              ))}
            </select>
          )}
        />
      )}
      {errors.reason && (
        <p className="text-alert text-sm mt-1">{errors.reason.message}</p>
      )}

      <Controller
        name="feedbackText"
        control={control}
        rules={{ required: feedbackConfig.validation.feedbackRequired }}
        render={({ field: { onChange, onBlur } }) => (
          <TextInput
            name="feedbackText"
            label={t('feedback.yourFeedback')}
            required
            multi
            placeholder={
              FEEDBACK_TYPES[defaultReason]?.placeholder ||
              DEFAULT_FEEDBACK_TYPE.placeholder
            }
            onChangeText={onChange}
            onBlur={onBlur}
            isError={!!errors.feedbackText}
            hint={errors?.feedbackText?.message}
          />
        )}
      />

      <FeedbackImageUpload
        image={image}
        onRemove={onRemoveImage}
        onUpload={onFileChange}
      />
      {errors.image && (
        <p className="text-alert text-sm mt-1">{errors.image.message}</p>
      )}

      <Button type="submit" primary className="w-full">
        {t('feedback.submit')}
      </Button>
    </form>
  )
}

const SubmissionStatus = ({ status, onClose }) => {
  const { t } = useTranslation()
  const feedbackConfig = t('feedback')

  if (status === 'submitting') {
    return (
      <div className="flex flex-col gap-6 items-center justify-center py-8">
        <Loading label={feedbackConfig.submission.submitting} size="lg" />
      </div>
    )
  }

  if (status === 'successful') {
    return (
      <div className="flex flex-col gap-6 items-center justify-center py-8">
        <p className="text-base font-semibold leading-5 tracking-tighter text-black text-center">
          {feedbackConfig.submission.success}
        </p>
        <Button onClick={onClose} primary>
          {feedbackConfig.submission.close}
        </Button>
      </div>
    )
  }

  return null
}

const FeedbackSheet = ({
  isSheetOpen,
  onClose,
  defaultReason,
  hideReasonSelector = false,
  onSubmitFeedback,
}) => {
  const { t } = useTranslation()
  const FEEDBACK_TYPES = t('feedback.types')
  const DEFAULT_FEEDBACK_TYPE = t('feedback.default')
  const feedbackConfig = t('feedback')

  const [submissionStatus, setSubmissionStatus] = useState('awaiting')
  const [compressionError, setCompressionError] = useState(null)
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      reason: defaultReason || '',
      feedbackText: '',
      image: null,
    },
  })

  const image = watch('image')

  const onSubmit = async (data) => {
    setSubmissionStatus('submitting')
    try {
      await onSubmitFeedback(data.reason, data.feedbackText, image?.signedId)
      setSubmissionStatus('successful')
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (error) {
      setSubmissionStatus('failed')
    }
  }

  const handleFileChange = (result) => {
    setCompressionError(null)
    setValue('image', result)
  }

  const handleRemoveImage = () => {
    setValue('image', null)
    setCompressionError(null)
  }

  const handleClose = () => {
    onClose()
    setSubmissionStatus('awaiting')
    setCompressionError(null)
    reset({
      reason: defaultReason || '',
      feedbackText: '',
      image: null,
    })
  }

  const getSheetTitle = () =>
    FEEDBACK_TYPES[defaultReason]?.title || DEFAULT_FEEDBACK_TYPE.title

  return (
    <Sheet
      title={getSheetTitle()}
      viewHeight={60}
      isSheetOpen={isSheetOpen}
      onClose={handleClose}
      canClose={submissionStatus !== 'submitting'}
    >
      {submissionStatus === 'submitting' ||
      submissionStatus === 'successful' ? (
        <SubmissionStatus status={submissionStatus} onClose={handleClose} />
      ) : (
        <>
          {submissionStatus === 'failed' && (
            <p className="text-alert text-sm font-semibold text-center">
              {feedbackConfig.submission.failed}
            </p>
          )}
          {compressionError && (
            <p className="text-alert text-sm font-semibold text-center">
              {compressionError}
            </p>
          )}
          <FeedbackForm
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit)}
            defaultReason={defaultReason}
            hideReasonSelector={hideReasonSelector}
            onFileChange={handleFileChange}
            image={image}
            onRemoveImage={handleRemoveImage}
          />
        </>
      )}
    </Sheet>
  )
}

export default FeedbackSheet
